import { useState } from 'react'
import { FormattedMessage, FormattedNumber, useIntl } from 'react-intl'
import { useQueryClient } from '@tanstack/react-query'
import { useStore } from 'zustand'

import { BanknotesIcon } from '@gto/icons-react/outline/banknotes'
import { CheckCircleIcon } from '@gto/icons-react/outline/check-circle'
import { CurrencyDollarIcon } from '@gto/icons-react/outline/currency-dollar'
import { LockClosedIcon } from '@gto/icons-react/outline/lock-closed'
import { Alert } from '@gto/ui/alert'
import { Badge } from '@gto/ui/badge'
import { Button } from '@gto/ui/button'
import { Paper } from '@gto/ui/paper'
import { Typography } from '@gto/ui/typography'

import {
  getGetMyPayoutsQueryKey,
  getGetMyStatsQueryKey,
  useGetMyPayouts,
  useGetMyStats,
  useRequestPayout,
} from '@/api/gen'
import { AppDialog } from '@/components/app-dialog'
import { DashboardKpiTile } from '@/components/dashboard-kpi-tile'
import { EmptyState } from '@/components/empty-state'
import { InlineQueryError } from '@/components/inline-query-error'
import { MutationError } from '@/components/mutation-error'
import { Page } from '@/components/page'
import { PayoutDetailsView } from '@/components/payout-details-view'
import { PayoutsTable } from '@/components/payouts-table'
import { StatsCard } from '@/components/stats-card'
import { useMutationErrorHandler } from '@/hooks/use-mutation-error-handler'
import { useReportQueryError } from '@/hooks/use-report-query-error'
import { store } from '@/store'
import { extractValidationDetail } from '@/utils/extract-validation-detail'
import { toAmount } from '@/utils/to-amount'

const PAYOUT_METHOD_LABELS: Record<string, string> = {
  PAYPAL: 'PayPal',
  WISE: 'Wise',
  BANK_TRANSFER: 'Bank Transfer',
  CRYPTO: 'Crypto',
  WIRE: 'Wire Transfer',
}

// ─── Request Payout Dialog ───────────────────────────────────────────────────

type RequestPayoutDialogProps = {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  loading: boolean
  amount: number
  hasError: boolean
  errorDetail: string | null
  payoutMethod: string | null
  payoutDetails: Record<string, string> | null
}

const RequestPayoutDialog = ({
  open,
  onClose,
  onConfirm,
  loading,
  amount,
  hasError,
  errorDetail,
  payoutMethod,
  payoutDetails,
}: RequestPayoutDialogProps) => (
  <AppDialog
    open={open}
    onClose={onClose}
  >
    <AppDialog.Header
      subtitle={<FormattedMessage id="payouts.confirmDescription" />}
      title={<FormattedMessage id="payouts.requestPayout" />}
    />

    <AppDialog.Body>
      <MutationError
        data-tst="payouts-request-error"
        detail={errorDetail}
        messageId="payouts.requestError"
        show={hasError}
      />

      <div className="flex flex-col items-center">
        <Typography
          color="primary"
          variant="h4"
        >
          <FormattedNumber
            currency="USD"
            style="currency"
            value={amount}
          />
        </Typography>
      </div>

      {payoutMethod != null && payoutDetails != null && (
        <Paper
          className="border border-divider bg-text-primary/[0.03] p-4"
          elevation="0"
        >
          <div className="flex items-center gap-2">
            <Typography
              color="text.secondary"
              variant="caption"
            >
              <FormattedMessage id="payouts.confirmMethod" />
            </Typography>
            <Badge color="secondary">{PAYOUT_METHOD_LABELS[payoutMethod] ?? payoutMethod}</Badge>
          </div>
          <PayoutDetailsView
            details={payoutDetails}
            method={payoutMethod}
          />
        </Paper>
      )}
    </AppDialog.Body>

    <AppDialog.Footer>
      <Button
        disabled={loading}
        onClick={onClose}
      >
        <FormattedMessage id="payouts.cancel" />
      </Button>
      <Button
        color="primary"
        disabled={loading}
        variant="contained"
        onClick={onConfirm}
      >
        <FormattedMessage id="payouts.confirm" />
      </Button>
    </AppDialog.Footer>
  </AppDialog>
)

// ─── Main Page ───────────────────────────────────────────────────────────────

export const PayoutsPage = () => {
  const { formatMessage } = useIntl()
  const partner = useStore(store, (s) => s.partner)
  const queryClient = useQueryClient()

  const [requestDialogOpen, setRequestDialogOpen] = useState(false)

  const statsQuery = useGetMyStats()
  const {
    data: stats,
    isLoading: statsLoading,
    isError: statsError,
    refetch: refetchStats,
  } = statsQuery
  const payoutsQuery = useGetMyPayouts()
  const {
    data: payouts,
    isLoading: payoutsLoading,
    isError: payoutsError,
    refetch: refetchPayouts,
  } = payoutsQuery

  // Forward load failures to Sentry — the bare app QueryClient has no global sink.
  useReportQueryError(statsQuery, 'getMyStats failed: ')
  useReportQueryError(payoutsQuery, 'getMyPayouts failed: ')

  // Money-critical: a failed payout request must reach Sentry AND surface to the
  // partner inside the dialog, and must NOT close the dialog (success-only
  // side-effect).
  // The BE raises three partner-actionable `CustomValidationError` details on a
  // 422 (no payout method / no available balance / pending request — see
  // `/v4/affiliates/me/payouts/` in gto-wizard). Sentry still gets the raw axios
  // error via `captureAxiosError`; this extractor only feeds the inline message
  // override so the partner sees the actionable reason instead of the generic
  // fallback. Any non-422 falls back to `payouts.requestError`.
  const requestError = useMutationErrorHandler('useRequestPayout failed: ', {
    extractDetail: extractValidationDetail,
  })

  const requestPayout = useRequestPayout({
    mutation: {
      onSuccess: () => {
        void queryClient.invalidateQueries({ queryKey: getGetMyPayoutsQueryKey() })
        void queryClient.invalidateQueries({ queryKey: getGetMyStatsQueryKey() })
        setRequestDialogOpen(false)
        requestError.clearError()
      },
      onError: requestError.onError,
    },
  })

  const metricsError = statsError || payoutsError

  const availableBalance = toAmount(stats?.balance_available_usd, 'payouts balance_available_usd:')
  const lockedBalance = toAmount(stats?.balance_locked_usd, 'payouts balance_locked_usd:')
  const paidBalance = toAmount(stats?.balance_paid_usd, 'payouts balance_paid_usd:')
  const hasPayoutMethod = partner?.payout_method != null && partner.payout_method !== ''
  const hasPendingPayout = (payouts ?? []).some((p) => p.status === 'REQUESTED')

  const rows = payouts ?? []

  return (
    <Page>
      <Page.Header
        title={<FormattedMessage id="payouts.title" />}
        actions={
          <Button
            variant="contained"
            // metricsError gates the CTA on BOTH queries deliberately:
            // hasPendingPayout is derived from the payout-history list, so a
            // failed list load means pending-payout state is unknown — keep
            // the money write locked until the inline Retry succeeds.
            disabled={
              availableBalance <= 0 ||
              !hasPayoutMethod ||
              hasPendingPayout ||
              requestPayout.isPending ||
              metricsError
            }
            startIcon={
              <CurrencyDollarIcon
                height={18}
                width={18}
              />
            }
            onClick={() => {
              requestError.clearError()
              setRequestDialogOpen(true)
            }}
          >
            <FormattedMessage id="payouts.requestPayout" />
          </Button>
        }
      />

      <Page.Content>
        {metricsError ? (
          <InlineQueryError
            data-tst="payouts-error"
            messageId="payouts.error"
            onRetry={() => {
              if (statsError) {
                void refetchStats()
              }
              if (payoutsError) {
                void refetchPayouts()
              }
            }}
          />
        ) : (
          <>
            {hasPendingPayout && (
              <Alert
                severity="info"
                message={
                  <FormattedMessage
                    defaultMessage="You already have a pending payout request. Please wait for it to be processed."
                    id="payouts.pendingPayoutExists"
                  />
                }
              />
            )}

            {!hasPayoutMethod && (
              <Alert
                message={<FormattedMessage id="payouts.noPayoutMethod" />}
                severity="warning"
              />
            )}

            <div
              className="flex flex-row flex-wrap gap-2"
              data-tst="kpi-strip"
            >
              <DashboardKpiTile
                icon={<BanknotesIcon />}
                label={<FormattedMessage id="payouts.availableForPayout" />}
                loading={statsLoading}
                tooltip={formatMessage({ id: 'tooltip.availableBalance' })}
                value={
                  <FormattedNumber
                    currency="USD"
                    style="currency"
                    value={availableBalance}
                  />
                }
              />
              <DashboardKpiTile
                icon={<LockClosedIcon />}
                label={<FormattedMessage id="payouts.lockedInGracePeriod" />}
                loading={statsLoading}
                tooltip={formatMessage({ id: 'tooltip.lockedBalance' })}
                value={
                  <FormattedNumber
                    currency="USD"
                    style="currency"
                    value={lockedBalance}
                  />
                }
              />
              <DashboardKpiTile
                icon={<CheckCircleIcon />}
                label={<FormattedMessage id="payouts.totalPaid" />}
                loading={statsLoading}
                tooltip={formatMessage({ id: 'tooltip.totalPaid' })}
                value={
                  <FormattedNumber
                    currency="USD"
                    style="currency"
                    value={paidBalance}
                  />
                }
              />
            </div>

            <StatsCard
              icon={<BanknotesIcon />}
              title={<FormattedMessage id="payouts.history" />}
            >
              {!payoutsLoading && rows.length === 0 ? (
                <EmptyState title={<FormattedMessage id="payouts.empty" />} />
              ) : (
                <PayoutsTable
                  data={rows}
                  getRowId={(row) => row.id}
                  loading={payoutsLoading}
                />
              )}
            </StatsCard>
          </>
        )}
      </Page.Content>

      <RequestPayoutDialog
        amount={availableBalance}
        errorDetail={requestError.detail}
        hasError={requestError.hasError}
        loading={requestPayout.isPending}
        open={requestDialogOpen}
        payoutDetails={partner?.payout_details ?? null}
        payoutMethod={partner?.payout_method ?? null}
        onConfirm={() => requestPayout.mutate()}
        onClose={() => {
          setRequestDialogOpen(false)
          requestError.clearError()
        }}
      />
    </Page>
  )
}
